import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import _ from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import PremiumReportApi from '../../api/PremiumReportingApi';
import { AuthData } from '../../context/auth';
import { ErrorData } from '../../context/ErrorProvider';
import { PolicyData } from '../../context/policy';
import {
	isPremiumReportUpdateReqChanged,
	PremiumReportData,
	withPremiumReportContext,
} from '../../context/premiumReport';
import {
	PREMIUM_REPORT_ISSUE_FAILURE,
	PREMIUM_REPORT_ISSUE_SUCCESS,
	PREMIUM_REPORT_REPLACE_FAILURE,
	PREMIUM_REPORT_REPLACE_SUCCESS,
	PREMIUM_REPORT_UI_UPDATE_FAILURE,
	PREMIUM_REPORT_UPDATE_FAILURE,
	PREMIUM_REPORT_UPDATE_SUCCESS,
} from '../../data/labelsAndMessages';
import DropdownButton from '../DropdownButton';
import { errorToast, successToast } from '../Toast';
import CancelReplaceModal from './modals/CancelReplaceModal';
import ReplaceReportModal from './modals/ReplaceReportModal';
import ReverseReportModal from './modals/ReverseReportModal';
import {
	getPremiumReportStartDate,
	getReportEndDateError,
	getTodaysDateWithTimezone,
} from './util';

interface Props {
	premiumReportData: PremiumReportData;
	authData: AuthData;
	errorData: ErrorData;
	policyData: PolicyData;
}

const PremiumReportActions = (props: Props) => {
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields, isDirty, errors } = formState;
	const { errorData, authData, premiumReportData, policyData } = props;
	const { hasPermission } = authData;
	const navigate = useNavigate();
	const {
		premiumReportUpdateReq,
		premiumReport,
		replaceMode,
		setLoading,
		setReplaceMode,
		setPremiumReportUpdateReq,
	} = premiumReportData;
	const isChanged =
		isDirty ||
		isPremiumReportUpdateReqChanged(premiumReportUpdateReq, premiumReport);

	// SOCA-10193 sort by end date
	const sortedPremiumReports = premiumReportData.premiumReports
		.filter((a) => a.state === 'issued')
		.sort((a, b) =>
			a.endTimestamp < b.endTimestamp
				? 1
				: b.endTimestamp < a.endTimestamp
				? -1
				: 0,
		);
	const isLatestPremiumReport =
		sortedPremiumReports.length > 0
			? sortedPremiumReports[0].locator === premiumReport.locator
			: false;

	const getPremiumReportUpdateReqErrors = () => {
		const { premiumReport, premiumReports, premiumReportUpdateReq } =
			premiumReportData;

		const reportStartDate = new Date(
			parseInt(
				getPremiumReportStartDate(
					premiumReport,
					premiumReports,
					policyData.policy,
				),
				10,
			),
		);
		const reportEndDate = premiumReportUpdateReq.endTimestamp
			? new Date(parseInt(premiumReportUpdateReq.endTimestamp, 10))
			: null;
		const reportEndDateError = getReportEndDateError(
			reportEndDate,
			reportStartDate,
			policyData.policy,
			authData,
		);

		return reportEndDateError;
	};
	const handleUIvalidation = (): boolean => {
		const premiumReportUpdateReqErrors = getPremiumReportUpdateReqErrors();
		if (!_.isEmpty(errors) || !premiumReportUpdateReqErrors?.isValid) {
			errorToast(PREMIUM_REPORT_UI_UPDATE_FAILURE());
			return false;
		}

		return true;
	};

	const onUpdate = async () => {
		if (!handleUIvalidation()) {
			return;
		}

		setLoading(true);
		const { authData, premiumReportData } = props;
		try {
			await premiumReportData.saveFastForm(getValues(), dirtyFields, reset, {});
			successToast(PREMIUM_REPORT_UPDATE_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, PREMIUM_REPORT_UPDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const issue = async () => {
		setLoading(true);
		const { authData, premiumReportData } = props;
		const { auth } = authData;
		// we have to update the premium report prior to issuing
		try {
			await premiumReportData.saveFastForm(getValues(), dirtyFields, reset, {});
			const issuedPremiumReport = await PremiumReportApi.issuePremiumReport(
				auth.authorizationToken,
				premiumReport.locator,
			);
			await premiumReportData.setPremiumReport(issuedPremiumReport.data);
			await premiumReportData.fetchPremiumReports(
				issuedPremiumReport.data.policyLocator,
			);
			successToast(PREMIUM_REPORT_ISSUE_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, PREMIUM_REPORT_ISSUE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const discard = async () => {
		setLoading(true);
		const { authData, premiumReportData } = props;
		const { auth } = authData;
		const { policyLocator } = premiumReport;
		// we have to update the premium report prior to issuing
		try {
			await PremiumReportApi.discardPremiumReport(
				auth.authorizationToken,
				premiumReport.locator,
			);
			// go back to the policy's premium report view
			navigate(`/policy/${policyLocator}/premiumReports`);
			premiumReportData.clear();
			successToast(PREMIUM_REPORT_REPLACE_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, PREMIUM_REPORT_REPLACE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const enableReplaceMode = async () => {
		await setReplaceMode(true);
		await setPremiumReportUpdateReq({
			invoiceDueTimestamp: `${getTodaysDateWithTimezone(
				authData.auth.timezone,
			).getTime()}`,
		});

		navigate(
			`/policy/${policyData.policy.displayId}/premiumReports/${premiumReport.locator}/details`,
		);
	};

	const renderDraftActions = () => {
		const hiddenOptions = [];
		hasPermission('premiumReport.discard') &&
			hiddenOptions.push({
				label: 'Discard Premium Report',
				callback: discard,
			});
		const buttons = (
			<Stack direction='row' spacing={1}>
				{hasPermission('premiumReport.update') && (
					<Button
						key='save'
						color='primary'
						variant='contained'
						onClick={!isChanged ? null : onUpdate}
						disabled={!isChanged}
					>
						Save
					</Button>
				)}
				{hasPermission('premiumReport.issue') && (
					<Button
						key={'LockAndPrice'}
						color='primary'
						onClick={issue}
						variant='contained'
					>
						Issue
					</Button>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
		return buttons;
	};

	const renderReplaceModeActions = () => {
		const buttons = (
			<Stack direction='row' spacing={1}>
				<CancelReplaceModal key='cancel-replace' />
				<ReplaceReportModal key='replace' />
			</Stack>
		);
		return buttons;
	};

	const renderIssuedActions = () => {
		let buttons = [];
		if (premiumReport.state === 'issued') {
			hasPermission('premiumReport.replace') &&
				buttons.push(
					<Button
						key={'replace'}
						variant='contained'
						color='primary'
						onClick={enableReplaceMode}
					>
						Replace
					</Button>,
				);
		}
		if (isLatestPremiumReport && hasPermission('premiumReport.reverse')) {
			buttons.push(<ReverseReportModal key='reverse' />);
		}
		return (
			<Stack direction='row' spacing={1}>
				{buttons}
			</Stack>
		);
	};

	if (premiumReport.state === 'draft') {
		return renderDraftActions();
	} else if (replaceMode) {
		return renderReplaceModeActions();
	} else {
		return renderIssuedActions();
	}
};

export default withPremiumReportContext<Props>(PremiumReportActions);
